// "Featured today" — compact preview surface for the Aura tab.
//
// Top-liked submissions appear first. The #1 look is shown as a spotlight
// card; the rest stream as a horizontal "Top creators" rail. Tapping any
// tile opens a fullscreen vertical reels viewer (Instagram-style) starting
// from the tapped item, where the viewer can like, share, DM, or try the
// template on themselves.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart, Share2, Wand2, Crown, Coins, Sparkles, Instagram, X, ChevronRight, Flame, Trophy,
} from "lucide-react";
import { useFeaturedFeed, type FeedItem } from "@/hooks/useFeaturedFeed";
import { useSignedImage } from "@/hooks/useSignedImage";
import { useIsPaidUser } from "@/hooks/useIsPaidUser";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { SubmitFeaturedLauncher } from "@/components/featured/SubmitFeaturedLauncher";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { ReelsFeedAdCard, AD_EVERY_N } from "@/components/aura/ReelsFeedAdCard";
import { isNativeApp } from "@/lib/platform";
import { GetTheAppInlineCard } from "@/components/GetTheAppInlineCard";

interface Props {
  className?: string;
  limit?: number;
  onSubmitOwn?: () => void;
  /** Open the viewer focused on this submission id (deep-link from History). */
  focusId?: string | null;
}

const fmtCount = (n: number): string => {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}m`;
};

const openInstagram = (handle: string) => {
  const url = `https://instagram.com/${handle}`;
  try {
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
  } catch {
    window.location.href = url;
  }
};

/* ---------------- Preview tiles (in-page) ---------------- */

const SpotlightCard = ({
  item,
  onOpen,
}: {
  item: FeedItem;
  onOpen: () => void;
}) => {
  const url = useSignedImage("featured-images", item.permanent_image_path);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative block aspect-[4/5] w-full overflow-hidden rounded-3xl bg-black ring-1 ring-white/10 shadow-elevated active:scale-[0.99] transition-transform"
    >
      {url ? (
        <img
          src={url}
          alt={item.caption ?? item.template_label ?? "Top featured look"}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full animate-pulse bg-muted" />
      )}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {/* Rank badge */}
      <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2.5 py-1 text-[11px] font-bold text-black shadow-md ring-1 ring-white/40">
        <Trophy className="h-3.5 w-3.5" />
        #1 Today
      </div>
      <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur-md ring-1 ring-white/15">
        <Heart className={cn("h-3.5 w-3.5", item.has_liked ? "fill-red-500 text-red-500" : "text-white")} />
        {fmtCount(item.like_count)}
      </div>

      <div className="absolute inset-x-0 bottom-0 px-4 pb-4 text-left text-white">
        <div className="flex items-center gap-2 text-[11px] font-semibold">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-[10px] font-bold uppercase ring-1 ring-white/30">
            {(item.creator_name?.[0] ?? "?").toUpperCase()}
          </span>
          <span className="drop-shadow truncate">{item.creator_name}</span>
          {item.template_label && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide backdrop-blur">
              {item.template_label}
            </span>
          )}
        </div>
        {item.caption && (
          <p className="mt-1.5 line-clamp-2 max-w-[85%] text-sm font-semibold leading-snug drop-shadow">
            {item.caption}
          </p>
        )}
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-bold backdrop-blur-md ring-1 ring-white/30">
          <Sparkles className="h-3 w-3" /> Tap to explore
        </div>
      </div>
    </button>
  );
};

const RailTile = ({
  item,
  rank,
  onOpen,
}: {
  item: FeedItem;
  rank: number;
  onOpen: () => void;
}) => {
  const url = useSignedImage("featured-images", item.permanent_image_path);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative h-[180px] w-[124px] shrink-0 overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 active:scale-95 transition-transform"
    >
      {url ? (
        <img
          src={url}
          alt={item.caption ?? "Featured look"}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full animate-pulse bg-muted" />
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      <div className="absolute left-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-black/65 px-1.5 text-[10px] font-bold text-white backdrop-blur ring-1 ring-white/20">
        #{rank}
      </div>
      <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/65 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur ring-1 ring-white/20">
        <Heart className={cn("h-2.5 w-2.5", item.has_liked && "fill-red-500 text-red-500")} />
        {fmtCount(item.like_count)}
      </div>

      <div className="absolute inset-x-0 bottom-0 px-2 pb-2 text-left text-white">
        <p className="truncate text-[11px] font-bold drop-shadow">{item.creator_name}</p>
        {item.template_label && (
          <p className="truncate text-[10px] font-medium opacity-90 drop-shadow">
            {item.template_label}
          </p>
        )}
      </div>
    </button>
  );
};

/* ---------------- Fullscreen reel viewer (inside modal) ---------------- */

const ReelCard = ({
  item,
  onToggleLike,
  onTryOn,
  onShare,
}: {
  item: FeedItem;
  onToggleLike: () => void;
  onTryOn: () => void;
  onShare: () => void;
}) => {
  const url = useSignedImage("featured-images", item.permanent_image_path);
  const [burst, setBurst] = useState(false);
  const lastTap = useRef(0);

  const handleImageTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 280) {
      if (!item.has_liked) onToggleLike();
      setBurst(true);
      window.setTimeout(() => setBurst(false), 600);
      lastTap.current = 0;
    } else {
      lastTap.current = now;
    }
  };

  return (
    <article
      className="relative h-full w-full snap-start snap-always overflow-hidden bg-black"
      style={{ contain: "layout paint size" }}
    >
      <button
        type="button"
        onClick={handleImageTap}
        aria-label="Featured look"
        className="absolute inset-0 block h-full w-full"
      >
        {url ? (
          <img src={url} alt={item.caption ?? "Featured look"} className="h-full w-full object-cover" draggable={false} />
        ) : (
          <div className="h-full w-full animate-pulse bg-muted" />
        )}
      </button>

      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/60 to-transparent" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {burst && (
        <Heart className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 animate-scale-in fill-red-500 text-red-500 drop-shadow-[0_0_24px_rgba(239,68,68,0.7)]" aria-hidden />
      )}

      <div className="absolute bottom-28 right-3 flex flex-col items-center gap-4">
        <button type="button" onClick={onToggleLike} className="flex flex-col items-center gap-1 text-white" aria-label="Like">
          <span className={cn(
            "flex h-11 w-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-md ring-1 ring-white/20 transition-all active:scale-90",
            item.has_liked && "bg-red-500/90 ring-red-300/60",
          )}>
            <Heart className={cn("h-5 w-5", item.has_liked ? "fill-white text-white" : "text-white")} />
          </span>
          <span className="text-[11px] font-bold tabular-nums drop-shadow">{fmtCount(item.like_count)}</span>
        </button>
        <button type="button" onClick={onTryOn} className="flex flex-col items-center gap-1 text-white" aria-label="Try this on me">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-md ring-1 ring-white/20 active:scale-90">
            <Wand2 className="h-5 w-5" />
          </span>
          <span className="text-[10px] font-semibold drop-shadow">Try it</span>
        </button>
        <button type="button" onClick={onShare} className="flex flex-col items-center gap-1 text-white" aria-label="Share">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/45 backdrop-blur-md ring-1 ring-white/20 active:scale-90">
            <Share2 className="h-5 w-5" />
          </span>
          <span className="text-[10px] font-semibold drop-shadow">Share</span>
        </button>
        {item.instagram_handle && (
          <button type="button" onClick={() => openInstagram(item.instagram_handle!)} className="flex flex-col items-center gap-1 text-white" aria-label="Instagram">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 via-fuchsia-500 to-amber-400 ring-1 ring-white/40 shadow-md active:scale-90">
              <Instagram className="h-5 w-5 text-white" />
            </span>
            <span className="text-[10px] font-semibold drop-shadow">DM</span>
          </button>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 px-4 pb-6 text-white">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-[11px] font-bold uppercase ring-1 ring-white/30">
            {(item.creator_name?.[0] ?? "?").toUpperCase()}
          </span>
          <span className="drop-shadow">{item.creator_name}</span>
          {item.template_label && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide backdrop-blur">
              {item.template_label}
            </span>
          )}
        </div>
        {item.caption && (
          <p className="mt-2 max-w-[80%] text-sm font-medium leading-snug drop-shadow line-clamp-3">
            {item.caption}
          </p>
        )}
      </div>
    </article>
  );
};

const EndCard = ({ onSubmitOwn, web = false }: { onSubmitOwn?: () => void; web?: boolean }) => {
  if (web) {
    return (
      <div className="h-full w-full snap-start snap-always">
        <GetTheAppInlineCard
          variant="feed-slide"
          message="Download FitAuraAI - Try On Clothes from the Google Play Store to keep scrolling the full feed."
        />
      </div>
    );
  }
  return (
    <article className="relative flex h-full w-full snap-start snap-always flex-col items-center justify-center gap-4 bg-gradient-to-br from-primary/25 via-background to-accent/20 px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-accent/20 text-primary ring-1 ring-primary/30">
        <Sparkles className="h-7 w-7" />
      </div>
      <div>
        <p className="text-base font-bold">You're all caught up</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Submit your best look to be Featured. Approved looks earn likes and rank you up.
        </p>
      </div>
      {onSubmitOwn && (
        <Button onClick={onSubmitOwn} className="rounded-full">
          <Crown className="mr-2 h-4 w-4" /> Submit yours
          <span className="ml-2 inline-flex items-center gap-1 text-[11px] opacity-90">
            <Coins className="h-3 w-3" /> 3
          </span>
        </Button>
      )}
    </article>
  );
};

const ReelsViewer = ({
  items,
  startIndex,
  onClose,
  onToggleLike,
  onSubmitOwn,
  isPaid,
  onHouseAdCta,
}: {
  items: FeedItem[];
  startIndex: number;
  onClose: () => void;
  onToggleLike: (id: string) => void;
  onSubmitOwn: () => void;
  isPaid: boolean | null;
  onHouseAdCta: (batchNumber: number) => void;
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(startIndex);

  // Free users: how many items have been unlocked. Starts at AD_EVERY_N.
  // Each rewarded watch adds another AD_EVERY_N. Paid users see all items
  // unlocked immediately (with scrollable house ads sprinkled in).
  const [unlockedCount, setUnlockedCount] = useState<number>(
    isPaid ? items.length : Math.min(AD_EVERY_N, items.length),
  );

  // Re-sync when paid status resolves after mount, or items change.
  useEffect(() => {
    if (isPaid) {
      setUnlockedCount(items.length);
    } else if (isPaid === false) {
      setUnlockedCount((cur) => Math.max(cur, Math.min(AD_EVERY_N, items.length)));
    }
  }, [isPaid, items.length]);

  // Build the entry list interleaving ad cards every AD_EVERY_N items.
  //   free users: items[0..unlockedCount-1] + gate card (if more locked)
  //   paid users: all items + house ad every AD_EVERY_N
  type Entry =
    | { kind: "item"; item: FeedItem; itemIndex: number }
    | { kind: "ad"; batchNumber: number };

  const entries: Entry[] = [];
  const visibleCount = isPaid ? items.length : Math.min(unlockedCount, items.length);
  for (let i = 0; i < visibleCount; i++) {
    entries.push({ kind: "item", item: items[i], itemIndex: i });
    const onBoundary = (i + 1) % AD_EVERY_N === 0;
    if (onBoundary) {
      const batchNumber = (i + 1) / AD_EVERY_N;
      // Paid users always get a house ad at the boundary.
      // Free users get the gate only if there are more items locked behind it.
      if (isPaid) {
        entries.push({ kind: "ad", batchNumber });
      } else if (i + 1 < items.length) {
        entries.push({ kind: "ad", batchNumber });
      }
    }
  }

  // Jump to the tapped card on open
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: startIndex * el.clientHeight, behavior: "auto" });
    });
  }, [startIndex]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setActive(Math.round(el.scrollTop / el.clientHeight));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const onTryOn = useCallback((item: FeedItem) => {
    onClose();
    navigate("/app/style", {
      state: {
        reuseFeatured: {
          kind: item.kind,
          template_label: item.template_label,
        },
      },
    });
  }, [navigate, onClose]);

  const onShare = useCallback(async (item: FeedItem) => {
    const shareUrl = `${window.location.origin}/app/aura`;
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (nav.share) {
        await nav.share({
          title: "FitAura — Featured look",
          text: item.caption ?? item.template_label ?? "Check out this featured look on FitAura",
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: "Link copied" });
    } catch { /* cancelled */ }
  }, [toast]);

  const handleUnlock = useCallback(() => {
    setUnlockedCount((cur) => Math.min(cur + AD_EVERY_N, items.length));
    // Auto-scroll one card forward so the user lands on the next look.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
    });
  }, [items.length]);

  // Total slides incl. end card (only after everything's unlocked / for paid)
  const showEndCard = isPaid ? true : unlockedCount >= items.length;
  const totalSlides = entries.length + (showEndCard ? 1 : 0);

  return (
    <div className="relative h-full w-full bg-black">
      {/* Top chrome */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between px-3 pt-[max(env(safe-area-inset-top),12px)] pb-2">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-[11px] font-bold text-white backdrop-blur-md ring-1 ring-white/15">
          <Flame className="h-3.5 w-3.5 text-amber-400" /> Featured today
          {totalSlides > 0 && (
            <span className="opacity-80">· {Math.min(active + 1, totalSlides)}/{totalSlides}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md ring-1 ring-white/15 active:scale-90"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="h-full w-full snap-y snap-mandatory overflow-y-auto overscroll-contain"
        style={{ scrollbarWidth: "none" }}
      >
        {entries.map((entry, idx) =>
          entry.kind === "item" ? (
            <div key={`item-${entry.item.id}`} className="h-full w-full">
              <ReelCard
                item={entry.item}
                onToggleLike={() => onToggleLike(entry.item.id)}
                onTryOn={() => onTryOn(entry.item)}
                onShare={() => void onShare(entry.item)}
              />
            </div>
          ) : isPaid ? (
            <div key={`ad-house-${entry.batchNumber}-${idx}`} className="h-full w-full">
              <ReelsFeedAdCard
                variant="house-ad"
                batchNumber={entry.batchNumber}
                onCtaTap={() => onHouseAdCta(entry.batchNumber)}
              />
            </div>
          ) : (
            <div key={`ad-gate-${entry.batchNumber}-${idx}`} className="h-full w-full">
              <ReelsFeedAdCard
                variant="rewarded-gate"
                batchNumber={entry.batchNumber}
                onUnlock={handleUnlock}
              />
            </div>
          ),
        )}
        {showEndCard && (
          <div className="h-full w-full">
            <EndCard
              web={!isNativeApp()}
              onSubmitOwn={() => { onClose(); onSubmitOwn(); }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Exported section ---------------- */

export const FeaturedReelsFeed = ({ className, limit = 30, onSubmitOwn, focusId }: Props) => {
  const { items: allItems, loading, toggleLike } = useFeaturedFeed(true, limit);
  const isPaid = useIsPaidUser();
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [viewerStart, setViewerStart] = useState<number | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);

  // Web (desktop + mobile web) — cap the feed at 5 items and the rail at 3
  // so users are pushed to install the Android app for the rest.
  const web = !isNativeApp();
  const items = web ? allItems.slice(0, 5) : allItems;

  const openSubmit = useCallback(() => {
    if (onSubmitOwn) onSubmitOwn();
    else setLauncherOpen(true);
  }, [onSubmitOwn]);

  const openViewerAt = (idx: number) => setViewerStart(idx);
  const closeViewer = () => setViewerStart(null);

  const handleHouseAdCta = useCallback((batchNumber: number) => {
    // Alternating house ad variants: odd → credit packs, even → submit own.
    closeViewer();
    if (batchNumber % 2 === 1) setBuyOpen(true);
    else openSubmit();
  }, [openSubmit]);

  // Deep-link from History → open the viewer focused on a specific submission
  // once feed items have loaded. Tracks which focusId we already honoured so
  // closing + re-focusing the same id later still works.
  const honoredFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusId || loading || items.length === 0) return;
    if (honoredFocusRef.current === focusId) return;
    const idx = items.findIndex((it) => it.id === focusId);
    if (idx >= 0) {
      honoredFocusRef.current = focusId;
      setViewerStart(idx);
    }
  }, [focusId, items, loading]);

  const spotlight = items[0];
  const rail = web ? items.slice(1, 4) : items.slice(1);

  return (
    <>
      <section className={cn("relative", className)} aria-label="Featured today">
        <div className="mb-2 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <h2 className="flex items-center gap-1.5 text-base font-bold tracking-tight">
              <Flame className="h-4 w-4 text-amber-500" /> Featured today
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Top creators first · Tap any look to explore
            </p>
          </div>
          <button
            type="button"
            onClick={openSubmit}
            className="flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-primary to-accent px-3 py-1.5 text-[11px] font-bold text-primary-foreground shadow-soft active:scale-95"
          >
            <Crown className="h-3.5 w-3.5" /> Get Featured
          </button>
        </div>

        {loading ? (
          <div className="aspect-[4/5] w-full animate-pulse rounded-3xl bg-muted" />
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6 text-center">
            <Sparkles className="mx-auto mb-2 h-6 w-6 text-primary" />
            <p className="text-sm font-bold">Be the first Featured today</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Submit your best look — approvals earn likes and climb the leaderboard.
            </p>
            <Button onClick={openSubmit} className="mt-3 rounded-full">
              <Crown className="mr-2 h-4 w-4" /> Submit yours
            </Button>
          </div>
        ) : (
          <>
            {spotlight && <SpotlightCard item={spotlight} onOpen={() => openViewerAt(0)} />}

            {rail.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    Top creators
                  </p>
                  <button
                    type="button"
                    onClick={() => openViewerAt(0)}
                    className="flex items-center gap-0.5 text-[11px] font-bold text-primary active:scale-95"
                  >
                    See all <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div
                  className="-mx-5 flex gap-2.5 overflow-x-auto px-5 pb-1"
                  style={{ scrollbarWidth: "none", scrollSnapType: "x mandatory" }}
                >
                  {rail.map((it, i) => (
                    <div key={it.id} style={{ scrollSnapAlign: "start" }}>
                      <RailTile item={it} rank={i + 2} onOpen={() => openViewerAt(i + 1)} />
                    </div>
                  ))}
                  {web && allItems.length > items.length && (
                    <div style={{ scrollSnapAlign: "start" }}>
                      <GetTheAppInlineCard variant="rail-tile" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <Dialog open={viewerStart !== null} onOpenChange={(o) => !o && closeViewer()}>
        <DialogContent
          className={cn(
            // Mobile: true fullscreen edge-to-edge reels.
            "max-w-none w-screen h-[100dvh] p-0 gap-0 border-0 rounded-none bg-black [&>button]:hidden",
            // Desktop: centered phone-shaped panel so the full portrait try-on is visible
            // without cropping into a 16:9 letterbox.
            "sm:w-[min(420px,90vw)] sm:h-[min(92dvh,820px)] sm:max-w-[420px] sm:rounded-3xl sm:overflow-hidden sm:shadow-2xl sm:ring-1 sm:ring-white/10",
          )}
          aria-describedby={undefined}
        >
          {viewerStart !== null && (
            <ReelsViewer
              items={items}
              startIndex={viewerStart}
              onClose={closeViewer}
              onToggleLike={(id) => void toggleLike(id)}
              onSubmitOwn={openSubmit}
              isPaid={isPaid}
              onHouseAdCta={handleHouseAdCta}
            />
          )}
        </DialogContent>
      </Dialog>

      <SubmitFeaturedLauncher open={launcherOpen} onOpenChange={setLauncherOpen} />
      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
    </>
  );
};
